import './upsell-modifier-carousel.css';

import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { IDataItem, IItemModifierDisplayGroup, IItemModifierDisplayGroupModifier } from '../../../models/data.interface';
import { IOrderItem, IOrderItemModifier } from '../../../models/order.interface';
import { IName } from '../../../interfaces/global.interfaces';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { LocalizationService } from '../../../services/localization-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { Util } from '../../../utils/util';

export interface IUpsellModifierCarouselParams {
    data: Data,
    order: Order,
    localizationService: LocalizationService,
    loyaltyProvider: ILoyaltyProvider,
    /** Called around an add (the cart re-renders in between), e.g. to keep the item's details open. */
    onAddStart?: (itemKey: number) => void,
    onAddEnd?: (itemKey: number) => void
}

export interface IUpsellModifierOption {
    names: IName[],
    price: number,
    modifier: IOrderItemModifier,
    /** Single-choice group (crust, sauce): the modifier replaces the group's current selection. */
    replacesSelection: boolean
}

/**
 * The cart item's "+ MODIFIER ($1.99)" carousel of back office modifier upsells.
 * Clicking one adds it to the item with the default pre-modifier (or swaps it in, for a single-choice group).
 */
export class UpsellModifierCarousel {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _localizationService: LocalizationService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _onAddStart?: (itemKey: number) => void;
    private readonly _onAddEnd?: (itemKey: number) => void;
    // One add at a time across every card: each add removes and re-applies the item's coupons.
    private _isAdding = false;

    constructor(params: IUpsellModifierCarouselParams) {
        this._data = params.data;
        this._order = params.order;
        this._localizationService = params.localizationService;
        this._loyaltyProvider = params.loyaltyProvider;
        this._onAddStart = params.onAddStart;
        this._onAddEnd = params.onAddEnd;
    }

    /**
     * Builds the carousel for a cart item (its halves share an ItemKey).
     * @returns The carousel, or null if the item has nothing to suggest.
     */
    public render(items: IOrderItem[]): HTMLElement | null {
        const options = this.getOptions(items);
        if (options.length === 0)
            return null;

        const container = Util.createHtmlElementFromTemplate(
            `<div class="upsell-modifier-carousel" role="group" aria-label="${this._localizationService.names("ARIA_UPSELL_MODIFIERS")}"></div>`);

        for (const option of options)
            container.appendChild(this._createOptionButton(items[0], option, container));

        return container;
    }

    /**
     * Upsell modifiers the item can take, in the store's sorting order: offered online on the item's size,
     * a Priced Modifier with a price, not already on the item, within the item's selection limits, and actually charged.
     */
    public getOptions(items: IOrderItem[]): IUpsellModifierOption[] {
        const upsellModifierIds = this._data.getProperty("UpsellModifierIds");
        if (items.length === 0 || upsellModifierIds.length === 0)
            return [];

        // Two-item half & half and loyalty-redeemed items get no carousel (redeeming fixes the price).
        if (items.some(i => i.ItemId !== items[0].ItemId || i.IsRedeemed))
            return [];

        // Only modifiers that are charged are shown (AC flowchart check 7): a meal deal discounts the item.
        if (this._isInMealDeal(items[0].ItemKey))
            return [];

        const mItem = this._data.getProperty("Items").find(x => x.ItemId === items[0].ItemId);
        if (!mItem)
            return [];

        const options: IUpsellModifierOption[] = [];
        for (const modifierId of upsellModifierIds) {
            // Any pre-modifier counts, including "No": the customer already chose for this modifier.
            if (items.some(i => i.Modifiers.some(m => m.ModifierId === modifierId)))
                continue;

            const option = this._getOption(mItem, items, modifierId);
            if (option)
                options.push(option);
        }

        return options;
    }

    // A meal deal is an applied coupon with qualifying items; order-level coupons don't count.
    private _isInMealDeal(itemKey: number): boolean {
        const couponKeys = new Set(this._order.getProperty("ItemsCoupons").filter(ic => ic.ItemKey === itemKey).map(ic => ic.CouponKey));
        if (couponKeys.size === 0)
            return false;

        const coupons = this._data.getProperty("Coupons");
        return this._order.getProperty("Coupons")
            .filter(c => couponKeys.has(c.CouponKey))
            .some(c => (coupons.find(x => x.CouponId === c.CouponId)?.Items.length ?? 0) > 0);
    }

    private _getOption(mItem: IDataItem, items: IOrderItem[], modifierId: number): IUpsellModifierOption | null {
        const sizeId = items[0].SizeId;
        const defaultPreModifierId = this._data.getProperty("DefaultPreModifierId");
        const orderPriceGroupId = this._order.getProperty("OrderTypeSubType").PriceGroupId;
        const defaultPriceGroupId = this._data.getProperty("DefaultPriceGroupId");
        const nonChargeableDisplayGroups = this._data.getProperty("Settings").NCDG;
        const preModifiers = this._data.getProperty("PreModifiers");

        for (const [groupIndex, group] of mItem.ModifierDisplayGroups.entries()) {
            const modifierIndex = group.Modifiers.findIndex(m => m.ModifierId === modifierId);
            if (modifierIndex === -1)
                continue;

            // Only the item's Priced Modifiers are suggested (an Upcharge-only modifier isn't).
            const iMod = group.Modifiers[modifierIndex];
            if (!iMod.CountForPricing
                || iMod.IsHiddenOnOrder
                || !OnlineOrderingUtil.isModOfferedOnSize(iMod, sizeId)
                || iMod.PreModifierException?.includes(defaultPreModifierId)
                || !this._isWithinLimits(mItem, items, group.ModifierDisplayGroupId))
                continue;

            const price = OnlineOrderingUtil.GetModifierPrice({
                mItem,
                sizeId,
                modifierId,
                modifierDisplayGroupId: group.ModifierDisplayGroupId,
                orderPriceGroupId,
                defaultPriceGroupId,
                nonChargeableDisplayGroups,
                defaultPreModifierId,
                preModifiers,
                excludeUpcharge: true
            });
            // The size's per-modifier price, without upcharges (AC); nothing to show when it's $0.
            if (price <= 0)
                continue;

            const modifier = this._createOrderModifier(group, groupIndex, iMod, modifierIndex, defaultPreModifierId);
            const replacesSelection = this._isSingleChoice(group.ModifierDisplayGroupId);

            // A free-modifier allowance or maximum price can absorb it; then it isn't charged (AC flowchart check 7).
            const isCharged = items.some(item => {
                const getPrice = (modifiers: IOrderItemModifier[]) => OnlineOrderingUtil.GetItemPrice(mItem, sizeId,
                    modifiers.map(m => ({ ...m })), orderPriceGroupId, defaultPriceGroupId, nonChargeableDisplayGroups, preModifiers).Price;

                return getPrice(OnlineOrderingUtil.WithUpsellModifier(item.Modifiers, modifier, replacesSelection)) > getPrice(item.Modifiers);
            });
            if (!isCharged)
                continue;

            return {
                names: this._data.getProperty("Modifiers").find(x => x.ModifierId === modifierId)?.Names ?? [],
                price,
                modifier,
                replacesSelection
            };
        }

        return null;
    }

    private _isSingleChoice(modifierDisplayGroupId: number): boolean {
        const group = this._data.getProperty("ModifierDisplayGroups").find(x => x.ModifierDisplayGroupId === modifierDisplayGroupId);
        return !!group && (group.MaxSelectionCount === 1 || group.MustToggle);
    }

    /**
     * Same limits Customize enforces when a modifier is added: a multi-select group at its maximum, or an item at its
     * maximum modifier count. A single-choice group is never full; the upsell replaces its selection.
     */
    private _isWithinLimits(mItem: IDataItem, items: IOrderItem[], modifierDisplayGroupId: number): boolean {
        const group = this._data.getProperty("ModifierDisplayGroups").find(x => x.ModifierDisplayGroupId === modifierDisplayGroupId);
        if (!group || this._isSingleChoice(modifierDisplayGroupId) || !group.IsVisible)
            return true;

        const nonePreModifierId = this._data.getProperty("NonePreModifierId");

        for (const item of items) {
            const groupCount = item.Modifiers.filter(m => m.ModifierDisplayGroupId === modifierDisplayGroupId && m.PreModifierId !== nonePreModifierId).length;

            if (group.MaxSelectionCount > 1 && groupCount >= group.MaxSelectionCount)
                return false;

            if (this._countLimitedModifiers(mItem, item) >= mItem.MaximumModifierCount)
                return false;
        }

        return true;
    }

    // Non-default modifiers in multi-select groups, as counted by Customize's item maximum.
    private _countLimitedModifiers(mItem: IDataItem, item: IOrderItem): number {
        const groups = this._data.getProperty("ModifierDisplayGroups");

        return item.Modifiers.filter(m => {
            if (m.IsDefault)
                return false;

            const groupId = mItem.ModifierDisplayGroups.find(g => g.Modifiers.some(x => x.ModifierId === m.ModifierId))?.ModifierDisplayGroupId;
            const group = groups.find(g => g.ModifierDisplayGroupId === groupId);
            return !!group && !group.MustToggle && group.MaxSelectionCount !== 1;
        }).length;
    }

    private _createOrderModifier(group: IItemModifierDisplayGroup, groupIndex: number, iMod: IItemModifierDisplayGroupModifier,
        modifierIndex: number, preModifierId: number): IOrderItemModifier {
        return {
            ModifierId: iMod.ModifierId,
            Name: iMod.Name,
            Index: iMod.Index,
            ModifierDisplayGroupIndex: group.Index,
            iIndex: modifierIndex,
            iModifierDisplayGroupIndex: groupIndex,
            PreModifierId: preModifierId,
            IsDefault: iMod.IsDefault,
            ModifierDisplayGroupId: group.ModifierDisplayGroupId,
            MergedModifierDisplayGroupId: iMod.MergedModifierDisplayGroupId,
            CountForPricing: iMod.CountForPricing,
            CountForPricingByItself: iMod.CountForPricingByItself,
            Price: 0,
            IsCrust: this._data.getProperty("Modifiers").find(x => x.ModifierId === iMod.ModifierId)?.IsCrust ?? false
        };
    }

    private _createOptionButton(item: IOrderItem, option: IUpsellModifierOption, container: HTMLElement): HTMLElement {
        const name = this._localizationService.GetName(option.names);
        const price = Util.formatMoney(option.price, this._data.getProperty("StoreInfo").country);
        const ariaLabel = this._localizationService.names("ARIA_UPSELL_ADD_MODIFIER")
            .replace("{{modifier_name}}", name)
            .replace("{{price}}", price);

        const button = Util.createHtmlElementFromTemplate(
            `<button type="button" class="upsell-modifier-option" aria-label="${ariaLabel}">+ ${name} (${price})</button>`);

        button.onclick = async () => {
            if (this._isAdding)
                return;

            // The cart re-renders the item once it's added.
            this._isAdding = true;
            container.querySelectorAll("button").forEach(b => b.disabled = true);

            let added = false;
            this._onAddStart?.(item.ItemKey);
            try {
                added = await this._order.addModifierByItemKeyAsync(this._loyaltyProvider, item.ItemKey, option.modifier, option.replacesSelection);
            }
            finally {
                this._onAddEnd?.(item.ItemKey);
                this._isAdding = false;
                if (!added)
                    container.querySelectorAll("button").forEach(b => b.disabled = false);
            }

            if (!added)
                return;

            const itemName = this._localizationService.GetName(this._data.getProperty("Items").find(x => x.ItemId === item.ItemId)?.Names ?? []);
            Util.announce(this._localizationService.names("ARIA_UPSELL_MODIFIER_ADDED")
                .replace("{{modifier_name}}", name)
                .replace("{{item_name}}", itemName));
        };

        return button;
    }
}
